import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  FeatureToggleEnum,
  PRODUCTS_BILLINFO_ROUTE,
  PRODUCTS_CONSUMPTION,
  PRODUCTS_ROUTE,
  TARIFF_CHANGE_PRODUCTS_ROUTE,
  TARIFF_CHANGE_ROUTE,
} from '@mwe/constants';
import { Address, ClearingAccount, ContractStatusEnum, createAddress, IPopupModel, Product, UnpaidInvoices } from '@mwe/models';
import {
  containsStromGasOrWaermeProducts,
  getProductCategories,
  isArrayWithMinOneItem,
  isContractStatus,
  isProductCategoryEmobilityOrInternet,
  removeAddressGroupName,
  setAddressGroupName,
  sortProductsByCategory,
} from '@mwe/utils';
import {
  AccountLogic,
  AddressGroupService,
  ClearingAccountLogic,
  ConsumptionStateService,
  InteractiveInvoicesLogic,
  InvoiceDataChangeLogic,
  MoveOutLogic,
  NavigationStateService,
  PopupService,
  ProductBillInfoStateService,
  productStore,
  ProfileService,
  RelocationStore,
  TariffChangeStateService,
} from '@mwe/services';
import { AddressComponent, AddressDropdownItem } from '../address/address.component';
import { HeadlineService } from '../headline/headline.service';
import { ProductsGroupUnpaidInvoiceNotificationComponent } from './products-group-unpaid-invoice-notification/products-group-unpaid-invoice-notification.component';
import { ProductsCardComponent } from '../product-card/products-card/products-card.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';

@Component({
  selector: 'mwe-products-group',
  templateUrl: './products-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AddressComponent, ProductsGroupUnpaidInvoiceNotificationComponent, ProductsCardComponent],
})
export class ProductsGroupComponent {
  public readonly headlineService = inject(HeadlineService); // public?
  private readonly popupService = inject(PopupService);
  private readonly addressGroupService = inject(AddressGroupService);
  private readonly profileService = inject(ProfileService);
  private readonly relocationStore = inject(RelocationStore);
  private readonly tariffChangeStateService = inject(TariffChangeStateService);
  private readonly productBillInfoStateService = inject(ProductBillInfoStateService);
  private readonly consumptionStateService = inject(ConsumptionStateService);
  private readonly router = inject(Router);
  private readonly invoiceDataChangeLogic = inject(InvoiceDataChangeLogic);
  private readonly moveOutLogic = inject(MoveOutLogic);
  private readonly interactiveInvoicesLogic = inject(InteractiveInvoicesLogic);
  private readonly clearingAccountLogic = inject(ClearingAccountLogic);
  private readonly navigationStateService = inject(NavigationStateService);
  private readonly accountLogic = inject(AccountLogic);
  private readonly productStore = inject(productStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly relocationDataReloaded = output<any>();
  readonly address = model<Address>();

  readonly addressIdx = input<string | number>('');
  readonly allowedUZorAZTooltipAddresses = input<string[]>();
  readonly productsInput = input<Product[]>();
  readonly state = input<'details' | 'list'>();
  readonly showCompanyCorrelation = input<boolean>(false);
  readonly checkIfFernwaermeIsActive = input<boolean>(false);
  readonly isClickableCard = input<boolean>(true);

  readonly dropdownItems = signal<AddressDropdownItem[]>([]);
  readonly featuredBtnIds = signal<string[]>([]);
  readonly dropdownType = signal<'pen' | 'dropdown' | 'custom' | 'dots' | string>('');
  readonly unpaidInvoices = signal<ClearingAccount[]>([]);
  readonly isDetailsState = computed<boolean>(() => this.state() === 'details');
  readonly productCategories = computed<string[]>(() => getProductCategories(this.products()));
  readonly hasUnpaidInvoices = computed<boolean>(() =>
    this.unpaidInvoices()?.some(ui => isArrayWithMinOneItem(ui?.unpaidInvoices?.offeneZahlungen)),
  );
  readonly hasContractEnded = computed<boolean>(() =>
    this.products().every(product => isContractStatus(product.vertragsStatus, ContractStatusEnum.Beendet)),
  );
  readonly products = computed<Product[]>(() => {
    let products: Product[] = [];
    if (this.productsInput() != null) {
      products = sortProductsByCategory(this.productsInput(), true) as Product[];
    }

    if (this.address()?.addressGroup?.detailsLinkType) {
      products = products.map(p => {
        return {
          ...p,
          address: this.address(),
        };
      });
    }
    return products;
  });

  constructor() {
    effect(() => {
      if (this.products() || this.address()) {
        this.dropdownItems.set([]);
        this.unpaidInvoices.set([]);
        this.featuredBtnIds.set([]);
        this.createDropDownItems();
        this.getAllUnpaidInvoices();
      }
    });
  }

  async saveGroupName(name: string): Promise<void> {
    const oldValue = this.address()?.addressGroup?.groupName;
    this.address.set(setAddressGroupName(this.address(), name));
    try {
      const addressGroup = await this.addressGroupService.setAddressGroupName(this.address().addressGroup);
      this.address.set(
        createAddress({
          ...this.address(),
          addressGroup,
        }),
      );
      this.productStore.updateAddressGroup(this.address());
    } catch (err: any) {
      this.address.set(setAddressGroupName(this.address(), oldValue));
      this.showErrorPopup();
      return err;
    }
  }

  actionHandler(item: string): void {
    switch (item) {
      case 'products.addresGroupItems.groupName':
        this.showGroupNamePopup();
        break;
      case 'products.addresGroupItems.relocation':
        this.relocationStore.initRelocationProcess(this.products());
        break;
      case 'tariffChange.entry':
        this.initTariffChangeProcess();
        break;
      case 'interactive-invoices.entry':
        this.interactiveInvoicesLogic.showInteractiveInvoices({
          address: this.address(),
          products: this.products(),
        });
        break;
      case 'products.addresGroupItems.costOverview':
        this.showBillinfoGroup();
        break;
      case 'consumption.menuItem':
        this.showConsumptionOverview();
        break;
      case 'relocation.status':
        this.relocationStore.initRelocationStatusProcess(this.address());
        break;
      case 'moveout.status':
        this.moveOutLogic.initMoveOutStatusProcess(this.address());
        break;
      case 'sepaInvoiceChange.entry':
        this.invoiceDataChangeLogic.startBankAccountChangeWithoutAddressSelection(this.products(), this.address());
        break;
      case 'pdfInvoiceChange.entryDropDown':
        this.invoiceDataChangeLogic.startPdfInvoiceChangeWithoutAddressSelection(this.products(), this.address());
        break;
      case 'move-out.entry':
        this.moveOutLogic.startProcessWithoutAddressSelection(this.products(), this.address());
        break;
      default:
      // ...
    }
  }

  initTariffChangeProcess(): void {
    this.tariffChangeStateService.directAddressSelection = true;
    this.tariffChangeStateService.tariffChangeAddress = createAddress(this.products()[0].address);
    this.tariffChangeStateService.tariffChangeProducts = this.products();
    this.router.navigateByUrl(`/${TARIFF_CHANGE_ROUTE}/${TARIFF_CHANGE_PRODUCTS_ROUTE}`);
  }

  showBillinfoGroup(): void {
    this.productBillInfoStateService.products = this.products();
    this.productBillInfoStateService.address = this.address();
    this.productBillInfoStateService.directAddressSelection = true;
    this.router.navigateByUrl(`/${PRODUCTS_ROUTE}/${PRODUCTS_BILLINFO_ROUTE}`);
  }

  showConsumptionOverview(): void {
    this.consumptionStateService.products = this.products();
    this.consumptionStateService.address = this.address();
    this.consumptionStateService.directAddressSelection = true;
    this.router.navigateByUrl(`/${PRODUCTS_ROUTE}/${PRODUCTS_CONSUMPTION}`);
  }

  showGroupNamePopup(): void {
    const model: IPopupModel = {
      id: 'products-group-group-name',
      titleKey: 'products.groupName.popup.title',
      messageKey: 'products.groupName.popup.message',
      showSubmitButton: true,
      submitButtonKey: 'products.groupName.popup.buttonOk',
      showCancelButton: true,
      cancelButtonKey: 'products.groupName.popup.buttonCancel',
      iconColorClass: 'blue',
      iconTypeClass: 'fa-pen',
      formModel: {
        inputs: [
          {
            name: 'groupName',
            initialValue: this.address()?.addressGroup?.groupName,
            labelKey: 'products.groupName.popup.labels.groupName',
            placeholder: 'products.groupName.popup.placeholders.groupName',
            validate: true,
            dataType: 'text',
            maxLength: 32,
          },
        ],
      },
    };
    this.popupService.open(model);
    this.popupService
      .events()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(async result => {
          if (!result || result === 'CANCEL') return;
          if ((result?.groupName && result?.groupName?.length) || !!this.address().addressGroup.detailsLinkType) {
            await this.saveGroupName(result.groupName.substring(0, 32));
            return;
          }
          if (this.address().addressGroup?.id) {
            await this.addressGroupService.deleteAddressGroupName(this.address().addressGroup);
            this.address.set(removeAddressGroupName(this.address()));
            this.productStore.updateAddressGroup(this.address());
          }
        }),
      )
      .subscribe();
  }

  showErrorPopup(): void {
    const model: IPopupModel = {
      id: 'products-group-error',
      titleKey: 'global.http.error.popup.title',
      messageKey: 'global.http.error.popup.message',
      showSubmitButton: true,
      submitButtonKey: 'global.http.error.popup.buttonOk',
      showCancelButton: false,
      iconColorClass: 'red',
    };
    this.popupService.open(model, true);
  }

  getUnpaidInvoice(product: Product): UnpaidInvoices {
    return this.unpaidInvoices().find(ui => {
      return (
        ui.accountId === product.accountNumber && ui.businessPartnerId === product.businessPartnerNumber && ui.systemId === product.systemId
      );
    })?.unpaidInvoices;
  }

  private async getAllUnpaidInvoices(): Promise<void> {
    if (this.profileService.isFeatureToggleDisabled(FeatureToggleEnum.DUE_PAYMENT_NOTIFICATIONS_ENABLED) || this.state() !== 'details') {
      return;
    }
    const emobilityAccounts = this.products()
      .filter(p => isProductCategoryEmobilityOrInternet(p.category))
      .map(p => p.accountNumber);
    // unpaid invoices is loaded from memory service and not backend. so no loading spinner is needed
    const unpaidInvoices = await this.clearingAccountLogic.getClearingAccountsForProducts(this.products());
    this.unpaidInvoices.set(unpaidInvoices);
    if (this.unpaidInvoices()) {
      this.unpaidInvoices.update(unpaidInvoices => {
        return unpaidInvoices.map(ui => {
          if (emobilityAccounts.includes(ui.accountId)) {
            // emobility + tk are not supported at the moment
            // can be removed once categories are supported
            return {
              ...ui,
              unpaidInvoices: null,
            };
          }

          return ui;
        });
      });
    }
  }

  private createDropDownItems(): void {
    if (!this.isDetailsState()) {
      return;
    }

    if (this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.RENAME_ADDRESS_GROUP_ENABLED)) {
      this.addToDropDownItems('products.addresGroupItems.groupName', this.accountLogic.isImpersonate());
    }

    this.dropdownType.set('dots');
    this.addToDropDownItems('interactive-invoices.entry');
    this.addToDropDownItems('tariffChange.entry');

    if (this.navigationStateService.navbarConfig.hasAllProcesses) {
      this.addToDropDownItems('products.addresGroupItems.costOverview');
    }

    if (containsStromGasOrWaermeProducts(this.products())) {
      this.addToDropDownItems('consumption.menuItem');
    }

    this.addToDropDownItems('pdfInvoiceChange.entryDropDown');
    this.addToDropDownItems('sepaInvoiceChange.entry');

    if (this.navigationStateService.navbarConfig.hasAllProcesses) {
      this.addToDropDownItems('products.addresGroupItems.relocation');
    }

    this.addToDropDownItems('move-out.entry');

    this.featuredBtnIds.set(['tariffChange.entry', 'interactive-invoices.entry']);
  }

  private addToDropDownItems(id: string, disabled?: boolean): void {
    const addressDropdownItem: AddressDropdownItem = { id: id, label: id };

    if (disabled) addressDropdownItem.disabled = disabled;

    this.dropdownItems.update(old => [...old, addressDropdownItem]);
  }
}
